// ============================================================================
// Beacon — operational layer. Your team (GC, inspector, estimator) submits
// costs in YOUR format as structured line items. Every estimate carries an
// automatic contingency buffer that protects you, and the buffered total flows
// straight into the deal's rehab number. Plus a court/permits/regulatory
// compliance checklist so nothing closes with an open liability.
// ============================================================================

import type { Deal, RehabItem, ComplianceItem, EstimatorRole } from "../types";

/** Rehab scopes — the categories a contractor/inspector fills in. */
export const REHAB_CATEGORIES = [
  "Roof",
  "HVAC",
  "Plumbing",
  "Electrical",
  "Kitchen",
  "Bath",
  "Flooring",
  "Windows & doors",
  "Exterior / siding",
  "Structural",
  "Foundation",
  "Permits & fees",
  "Landscaping / site",
  "Appliances",
  "Other",
] as const;

export const ESTIMATOR_ROLES: EstimatorRole[] = ["Contractor", "Inspector", "Estimator", "Owner"];

/** The buffer added to every estimate — protects you from under-quotes. */
export const DEFAULT_CONTINGENCY = 0.1;

/** Sum of raw line-item costs, before contingency. */
export function rehabSubtotal(items: RehabItem[] = []): number {
  return items.reduce((s, i) => s + (Number(i.cost) || 0), 0);
}

/** Contingency (buffer) applied on top of the subtotal. */
export function contingencyAmount(items: RehabItem[] = [], pct = DEFAULT_CONTINGENCY): number {
  return Math.round(rehabSubtotal(items) * pct);
}

/** Buffered rehab total — subtotal + contingency. This feeds Deal.rehab. */
export function rehabTotal(items: RehabItem[] = [], pct = DEFAULT_CONTINGENCY): number {
  return Math.round(rehabSubtotal(items) * (1 + pct));
}

/** The default compliance gates every deal must clear before closing. */
export function defaultCompliance(): ComplianceItem[] {
  return [
    { id: "title", label: "Title & liens clear (court records)", status: "todo" },
    { id: "litigation", label: "No open litigation / lis pendens", status: "todo" },
    { id: "permits", label: "Permits pulled / work is permittable", status: "todo" },
    { id: "code", label: "Zoning & building-code compliance (regulatory)", status: "todo" },
    { id: "inspection", label: "Independent inspection complete", status: "todo" },
    { id: "environmental", label: "Environmental & insurability cleared", status: "todo" },
  ];
}

/** Merge stored compliance with the default set so new gates always appear. */
export function withCompliance(deal: Deal): ComplianceItem[] {
  const stored = deal.compliance ?? [];
  const byId = new Map(stored.map((c) => [c.id, c]));
  return defaultCompliance().map((d) => byId.get(d.id) ?? d);
}

let seq = 0;
/** Stable-enough id without Date.now/Math.random (kept deterministic-friendly). */
export function newItemId(): string {
  seq += 1;
  return "ri_" + seq.toString(36) + "_" + performance.now().toString(36).replace(".", "");
}

export function blankItem(): RehabItem {
  return { id: newItemId(), category: "Roof", label: "", cost: 0, source: "", role: "Contractor", notes: "" };
}

/** How much of the compliance checklist is resolved (clear or flagged). */
export function complianceProgress(items: ComplianceItem[]): { clear: number; issues: number; total: number } {
  return {
    clear: items.filter((c) => c.status === "clear").length,
    issues: items.filter((c) => c.status === "issue").length,
    total: items.length,
  };
}
